import { buttonStyle } from '@florencelabs/button';
import { twMerge } from '@florencelabs/fabric-styles';
import { MaterialSymbol } from '@florencelabs/icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { PhoneLink } from '@provider-directory/components/phone-number';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import {
  getMapsLinkByName,
  getLocationDisplayInfo,
} from '@provider-directory/functions/location';
import {
  ProviderDetailsResource,
  ProviderLocationResource,
} from '@provider-directory/types/providers.contracts';

export const ProviderDetailsLocation: FC<
  ProviderLocationResource & { provider: ProviderDetailsResource }
> = ({ provider, ...location }) => {
  const { t } = useTranslation();
  const orgContext = useOrgContext();
  const phone = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );
  const analytics = useAnalytics();
  const { displayName, addressLabel } = getLocationDisplayInfo(location);

  return (
    <li
      className={
        'flex flex-col justify-between gap-8 overflow-hidden rounded-lg bg-primary-50 p-4 tablet:p-8'
      }
    >
      <div
        className={'flex flex-col gap-y-1'}
        data-testid={'location-card-address'}
      >
        {location.isPrimaryLocation && (
          <span className={'mb-1 text-body-small text-gray-500'}>
            {t('details.primaryLocation')}
          </span>
        )}
        <span
          className={
            'text-body-large-semibold leading-[180%] tracking-[-0.18px]'
          }
        >
          {displayName}
        </span>
        {location.name && <span>{location.address.street}</span>}
        {location.address.street2 && <span>{location.address.street2}</span>}
        <span className={'text-body-medium leading-[180%] tracking-[-0.18px]'}>
          {location.address.city},{' '}
          {location.address.stateCode || location.address.state}{' '}
          {location.address.zip}
        </span>
      </div>
      <div
        className={
          'flex w-full flex-col items-center justify-between gap-y-4 desktop:flex-row'
        }
      >
        {phone && (
          <PhoneLink
            phone={phone}
            format={'stripped'}
            onClick={(e) => {
              e.preventDefault();
              analytics.calledEvent({
                provider,
                phoneNumber: phone,
                linkContextPage: 'ProviderDetails',
                linkContextComponent: 'ProviderLocations',
                clickText: phone,
                clickURL: e.currentTarget.href,
              });
              window.open(e.currentTarget.href);
            }}
            aria-label={t('aria.callLocation', { locationName: displayName })}
            className={
              'flex w-full items-center justify-center gap-2 rounded-full border border-gray-200 p-3 !text-body-medium !leading-[140%] !tracking-[-0.32px] desktop:w-auto desktop:justify-start desktop:border-none desktop:pl-0'
            }
            data-testid={'location-button-phone-number'}
          >
            {({ formattedPhone }) => (
              <>
                <MaterialSymbol name={'call'} size={16} aria-hidden={'true'} />
                <span>{formattedPhone}</span>
              </>
            )}
          </PhoneLink>
        )}
        <a
          onClick={(e) => {
            e.preventDefault();
            analytics.directionsEvent({
              provider,
              phoneNumber: phone,
              linkContextPage: 'ProviderDetails',
              linkContextComponent: 'ProviderLocations',
              clickText: t('details.getDirections'),
              clickURL: e.currentTarget.href,
            });
            window.open(e.currentTarget.href);
          }}
          href={getMapsLinkByName(location)}
          rel={'noopener noreferrer'}
          aria-label={t('aria.getDirectionsTo', {
            locationName: displayName,
            address: addressLabel,
          })}
          className={twMerge(
            buttonStyle({ variant: 'clientTonal' }),
            'w-full desktop:w-auto',
          )}
          target={'_blank'}
          data-testid={'location-button-get-directions'}
        >
          {t('details.getDirections')}
        </a>
      </div>
    </li>
  );
};
